import { supabase } from '../lib/supabase';
import type { ActionEventRecord, ActionRecord } from '../types/actions';
import type { ClosureEventRecord } from '../types/close';
import type { AssessmentRecord, InspectionResponseRecord } from '../types/inspection';
import type { EvidenceRecord, IssueRecord } from '../types/issues';
import type { SavedProperty } from '../types/property';
import type { InspectionReportSnapshot, ReportIssue } from '../types/report';

const PROPERTY_SELECT =
  'id, property_type, developer_id, project_id, manual_developer_name, manual_project_name, locality, tower_or_block, unit_or_villa_number, bedrooms, bathrooms, inspection_stage, updated_at';
const ASSESSMENT_SELECT =
  'id, property_id, checklist_version, status, started_at, completed_at, updated_at';
const RESPONSE_SELECT =
  'id, assessment_id, question_id, answer, note, severity, updated_at';
const ISSUE_SELECT =
  'id, assessment_id, response_id, title, description, location, priority, status, created_at, updated_at';
const EVIDENCE_SELECT =
  'id, issue_id, storage_path, file_name, mime_type, size_bytes, evidence_type, note, captured_at, created_at';
const ACTION_SELECT =
  'id, issue_id, requested_action, accountable_party, accountable_party_name, priority, due_date, follow_up_date, status, created_at, updated_at';
const ACTION_EVENT_SELECT =
  'id, action_id, event_type, from_status, to_status, note, created_at';
const CLOSURE_SELECT =
  'id, issue_id, action_id, closure_evidence_id, waiver_acknowledged, waiver_reason, closure_note, created_at';

export async function loadInspectionReportSnapshot(
  ownerId: string,
  assessmentId: string,
): Promise<InspectionReportSnapshot> {
  const { data: assessmentData, error: assessmentError } = await supabase
    .from('assessments')
    .select(ASSESSMENT_SELECT)
    .eq('owner_id', ownerId)
    .eq('id', assessmentId)
    .single();
  if (assessmentError) throw assessmentError;
  const assessment = assessmentData as AssessmentRecord;

  const { data: propertyData, error: propertyError } = await supabase
    .from('properties')
    .select(PROPERTY_SELECT)
    .eq('owner_id', ownerId)
    .eq('id', assessment.property_id)
    .single();
  if (propertyError) throw propertyError;
  const property = propertyData as SavedProperty;

  const [{ data: responseData, error: responseError }, { data: issueData, error: issueError }] =
    await Promise.all([
      supabase
        .from('responses')
        .select(RESPONSE_SELECT)
        .eq('owner_id', ownerId)
        .eq('assessment_id', assessmentId)
        .order('updated_at', { ascending: true }),
      supabase
        .from('issues')
        .select(ISSUE_SELECT)
        .eq('owner_id', ownerId)
        .eq('assessment_id', assessmentId)
        .order('created_at', { ascending: true }),
    ]);

  if (responseError) throw responseError;
  if (issueError) throw issueError;

  const issues = (issueData ?? []) as IssueRecord[];
  const issueIds = issues.map((issue) => issue.id);

  let evidence: EvidenceRecord[] = [];
  let actions: ActionRecord[] = [];

  if (issueIds.length > 0) {
    const [{ data: evidenceData, error: evidenceError }, { data: actionData, error: actionError }] =
      await Promise.all([
        supabase
          .from('evidence')
          .select(EVIDENCE_SELECT)
          .eq('owner_id', ownerId)
          .in('issue_id', issueIds)
          .order('created_at', { ascending: true }),
        supabase
          .from('actions')
          .select(ACTION_SELECT)
          .eq('owner_id', ownerId)
          .in('issue_id', issueIds)
          .order('created_at', { ascending: true }),
      ]);

    if (evidenceError) throw evidenceError;
    if (actionError) throw actionError;
    evidence = (evidenceData ?? []) as EvidenceRecord[];
    actions = (actionData ?? []) as ActionRecord[];
  }

  const actionIds = actions.map((action) => action.id);
  let actionEvents: ActionEventRecord[] = [];
  let closureEvents: ClosureEventRecord[] = [];

  if (actionIds.length > 0) {
    const [{ data: eventData, error: eventError }, { data: closureData, error: closureError }] =
      await Promise.all([
        supabase
          .from('action_events')
          .select(ACTION_EVENT_SELECT)
          .eq('owner_id', ownerId)
          .in('action_id', actionIds)
          .order('created_at', { ascending: true }),
        supabase
          .from('closure_events')
          .select(CLOSURE_SELECT)
          .eq('owner_id', ownerId)
          .in('action_id', actionIds)
          .order('created_at', { ascending: true }),
      ]);

    if (eventError) throw eventError;
    if (closureError) throw closureError;
    actionEvents = (eventData ?? []) as ActionEventRecord[];
    closureEvents = (closureData ?? []) as ClosureEventRecord[];
  }

  const actionByIssue = new Map(actions.map((action) => [action.issue_id, action]));
  const reportIssues: ReportIssue[] = issues.map((issue) => {
    const action = actionByIssue.get(issue.id) ?? null;
    return {
      ...issue,
      evidence: evidence.filter((item) => item.issue_id === issue.id),
      action,
      actionEvents: action ? actionEvents.filter((event) => event.action_id === action.id) : [],
      closureEvents: action ? closureEvents.filter((event) => event.action_id === action.id) : [],
    };
  });

  let developerName: string | null = null;
  let projectName: string | null = null;

  if (property.developer_id) {
    const { data } = await supabase
      .from('developers')
      .select('display_name')
      .eq('id', property.developer_id)
      .maybeSingle();
    developerName = data?.display_name ?? null;
  }

  if (property.project_id) {
    const { data } = await supabase
      .from('projects')
      .select('project_name')
      .eq('id', property.project_id)
      .maybeSingle();
    projectName = data?.project_name ?? null;
  }

  return {
    property,
    developerName,
    projectName,
    assessment,
    responses: (responseData ?? []) as InspectionResponseRecord[],
    issues: reportIssues,
    generatedAt: new Date().toISOString(),
  };
}
